import { useState, useMemo, useEffect } from 'react';
import { Search, Plus, FileText, Loader2, Clock, MessageSquarePlus, Crown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AVAILABLE_TASKS, TASK_CATEGORY_INFO, TASK_TO_CATEGORY, TASK_CATEGORIES, Task } from '@/types/taskTypes';
import { UniversalTaskPanel } from './UniversalTaskPanel';
import { taskApi, userApi } from '@/services/api';
import { useNotificationStore, useSessionStore } from '@/stores';
import { useAuth } from '@/context/AuthContext';
import type { TaskResultAttachment } from '@/stores/sessionStore';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import PartsListDisplay from './task-displays/PartsListDisplay';
import SpecMapDisplay from './task-displays/SpecMapDisplay';
import ClaimsSupportDisplay from './task-displays/ClaimsSupportDisplay';
import ClaimsWithNumeralsDisplay from './task-displays/ClaimsWithNumeralsDisplay';
import CustomAnalysisDisplay from './task-displays/CustomAnalysisDisplay';
import IRMetadataDisplay from './task-displays/IRMetadataDisplay';
import IRInnovationAnalysisDisplay from './task-displays/IRInnovationAnalysisDisplay';
import IRTechClassificationDisplay from './task-displays/IRTechClassificationDisplay';
import DraftSummaryDisplay from './task-displays/DraftSummaryDisplay';
import DraftDetailedDescriptionDisplay from './task-displays/DraftDetailedDescriptionDisplay';
import DraftBackgroundDisplay from './task-displays/DraftBackgroundDisplay';
import DraftClaimsDisplay from './task-displays/DraftClaimsDisplay';
import OtherEmbodimentsDisplay from './task-displays/OtherEmbodimentsDisplay';
import OAExtractDisplay from './task-displays/OAExtractDisplay';
import OAAnalyzeDisplay from './task-displays/OAAnalyzeDisplay';
import OASummaryArtDisplay from './task-displays/OASummaryArtDisplay';
import OATimelineDisplay from './task-displays/OATimelineDisplay';
import TranslateDocumentDisplay from './task-displays/TranslateDocumentDisplay';
import LaTeXConvertDisplay from './task-displays/LaTeXConvertDisplay';
import OCRPdfDisplay from './task-displays/OCRPdfDisplay';
import AntecedentBasisDisplay from './task-displays/AntecedentBasisDisplay';
import TechnicalOutlineDisplay from './task-displays/TechnicalOutlineDisplay';
import PartsProcessOutlineDisplay from './task-displays/PartsProcessOutlineDisplay';

export function TasksPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskList, setShowTaskList] = useState(true);
  const [allTaskHistory, setAllTaskHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState<any>(null);
  const [accountInfo, setAccountInfo] = useState<any>(null);
  const { addNotification } = useNotificationStore();
  const { user } = useAuth();
  const addTaskAttachment = useSessionStore(state => state.addTaskAttachment);
  const [showTaskInfoModal, setShowTaskInfoModal] = useState(false);

  // Categorize tasks
  const categorizedTasks = useMemo(() => {
    const categories: Record<string, { name: string; icon: any; tasks: any[] }> = {};

    Object.entries(TASK_CATEGORY_INFO).forEach(([key, info]) => {
      // Skip form_processing category
      if (key === 'form_processing') return;
      categories[key] = { name: info.name, icon: info.icon, tasks: [] };
    });

    AVAILABLE_TASKS.forEach(task => {
      if (task.hidden || task.coming_soon) return;
      const category = task.task_type ? TASK_TO_CATEGORY[task.id] : 'legacy';
      const categoryKey = category || 'legacy';

      // Skip form_processing tasks
      if (categoryKey === 'form_processing') return;

      if (categories[categoryKey]) {
        categories[categoryKey].tasks.push(task);
      }
      categories.all.tasks.push(task);
    });

    return categories;
  }, []);

  // Fetch all task history when component mounts or when selectedTask changes
  useEffect(() => {
    const loadAllTaskHistory = async () => {
      if (selectedTask) {
        // Don't load all history when a task is selected
        return;
      }

      setHistoryLoading(true);
      try {
        // Get all task history (no filter)
        const response: any = await taskApi.getTaskHistory();
        const allHistory = response.history || [];

        // Filter out custom_question tasks - these belong in Document Viewer question history, not Tasks page
        const filteredHistory = allHistory.filter((item: any) =>
          item.task_type !== 'custom_question'
        );

        // Sort by newest first
        const sortedHistory = filteredHistory.sort((a: any, b: any) => {
          const dateA = new Date(a.time_received || a.timestamp).getTime();
          const dateB = new Date(b.time_received || b.timestamp).getTime();
          return dateB - dateA;
        });

        setAllTaskHistory(sortedHistory);
      } catch (error) {
        console.error('Failed to load all task history:', error);
        setAllTaskHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadAllTaskHistory();
  }, [selectedTask]);

  // Fetch account info when component mounts
  useEffect(() => {
    const fetchAccountInfo = async () => {
      if (!user) return;
      try {
        const response = await userApi.fetchAccountInfo();
        if (response && !response.error) {
          setAccountInfo(response);
        }
      } catch (error) {
        console.error('Failed to fetch account info:', error);
      }
    };
    fetchAccountInfo();
  }, [user]);

  // Listen for task selection from wizard
  useEffect(() => {
    const handleSelectTask = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { taskId } = customEvent.detail;
      console.log('[TasksPage] Received select-task event for:', taskId);

      const task = AVAILABLE_TASKS.find(t => t.id === taskId);
      if (task) {
        console.log('[TasksPage] Found task:', task.name);
        setSelectedTask(task);
        setSelectedHistoryItem(null);
      } else {
        console.warn('[TasksPage] Task not found:', taskId);
      }
    };

    window.addEventListener('select-task', handleSelectTask);
    return () => window.removeEventListener('select-task', handleSelectTask);
  }, []);

  // Filter tasks
  const filteredTasks = useMemo(() => {
    const categoryTasks = selectedCategory === 'all'
      ? categorizedTasks.all.tasks
      : categorizedTasks[selectedCategory]?.tasks || [];

    // Filter out hidden and coming soon tasks
    const visibleTasks = categoryTasks.filter(task => !task.hidden && !task.coming_soon);

    if (!searchTerm) return visibleTasks;

    return visibleTasks.filter(task =>
      task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [categorizedTasks, selectedCategory, searchTerm]);

  const getCategoryColor = (category: string) => {
    if (category === 'all') return 'gray';
    return TASK_CATEGORY_INFO[category]?.color || 'gray';
  };

  const getDocumentIdFromResult = (taskResult: any, fallbackId?: string): string | undefined => {
    if (!taskResult) return fallbackId;

    const candidateFields = [
      'document_id',
      'doc_id',
      'docId',
      'documentId',
      'doc_no',
      'docNo',
      'selected_number',
      'selectedNumber',
      'selected_doc_id',
      'selectedDocId',
      'selected_document',
      'selectedDocument',
      'document',
      'doc',
      'application_number',
      'applicationNumber',
      'resource_id',
      'resourceId',
    ];

    const extractFrom = (source: any): string | undefined => {
      if (!source) return undefined;
      for (const field of candidateFields) {
        const value = source[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          return value.trim();
        }
        if (typeof value === 'number') {
          return value.toString();
        }
      }
      return undefined;
    };

    return extractFrom(taskResult) ||
           extractFrom(taskResult?.metadata) ||
           (fallbackId ? fallbackId.trim() : undefined);
  };

  // Handle adding task result to chat
  const handleAddToChat = (taskResult: any, taskName: string, taskType: string, timestamp: string) => {
    // Extract and format the content
    const resultData = getParsedResult(getResultData(taskResult));
    const contentString = typeof resultData === 'string'
      ? resultData
      : JSON.stringify(resultData, null, 2);
    const documentId = getDocumentIdFromResult(taskResult);

    // Create task attachment
    const attachment: TaskResultAttachment = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      taskName: taskName,
      taskType: taskType,
      timestamp: timestamp,
      content: contentString,
      documentId,
    };

    // Add to global store
    addTaskAttachment(attachment);

    // Show notification
    addNotification({
      type: 'success',
      title: 'Added to Chat',
      message: `${taskName} result has been added to your chat input.`,
      duration: 3000,
    });
  };

  // Helper function to format timestamps correctly in local time
  const formatTimestamp = (timestamp: string | Date | undefined): string => {
    if (!timestamp) return 'Unknown time';

    try {
      let dateStr = timestamp;

      // If timestamp is a string, check if it has a timezone indicator
      if (typeof dateStr === 'string') {
        // Check if timestamp has timezone indicator (Z, +HH:MM, or -HH:MM)
        const hasTimezone = /[Z]$|[+-]\d{2}:\d{2}$/.test(dateStr);

        // If no timezone indicator, assume UTC and add 'Z'
        if (!hasTimezone && dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
          // Remove any decimal seconds first
          const baseTimestamp = dateStr.split('.')[0];
          dateStr = baseTimestamp + 'Z';
        }
        // If timestamp has microseconds, truncate to milliseconds
        else if (dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 2) {
            const fractionalPart = parts[1];
            // Check if there's more than 3 digits before timezone indicator
            const timezoneMatch = fractionalPart.match(/([+-]\d{2}:\d{2}|Z)$/);
            if (timezoneMatch) {
              const timezone = timezoneMatch[0];
              const digits = fractionalPart.substring(0, fractionalPart.indexOf(timezone));
              if (digits.length > 3) {
                // Truncate to 3 digits (milliseconds)
                dateStr = parts[0] + '.' + digits.substring(0, 3) + timezone;
              }
            }
          }
        }
      }

      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', timestamp, error);
      return String(timestamp);
    }
  };

  // Extract result data from result object, checking multiple possible properties
  const getResultData = (resultObj: any) => {
    if (!resultObj) return null;

    // Check multiple possible property names where the actual data might be stored
    // Priority: result > output > analysis > use the object itself
    if (resultObj.result !== undefined) return resultObj.result;
    if (resultObj.output !== undefined) return resultObj.output;
    if (resultObj.analysis !== undefined) return resultObj.analysis;

    // If none of the above exist, assume the object itself is the data
    return resultObj;
  };

  // Parse result output - it comes as a string that may need parsing
  const getParsedResult = (output: any) => {
    if (!output) return null;

    // If it's already an object, return it
    if (typeof output === 'object') return output;

    // If it's a string, try to parse it as JSON
    if (typeof output === 'string') {
      // Check if it looks like a Python dictionary (starts with { and contains single quotes)
      if (!output.trim().startsWith('{') || !output.includes("'")) {
        return output;
      }

      try {
        // Try to parse as JSON first
        return JSON.parse(output);
      } catch {
        // If parsing fails, try to evaluate as Python dict string
        try {
          // More robust Python to JSON conversion
          // Handle single quotes more carefully to avoid breaking contractions
          let jsonStr = output;

          // Replace single quotes only in structural positions (keys, values, arrays)
          jsonStr = jsonStr.replace(/'([^']*)':/g, '"$1":');  // Keys
          jsonStr = jsonStr.replace(/:\s*'([^']*)'/g, ': "$1"');  // Values after colons
          jsonStr = jsonStr.replace(/\[\s*'([^']*)'/g, '["$1"');  // Array first item
          jsonStr = jsonStr.replace(/,\s*'([^']*)'/g, ', "$1"');  // Array other items
          jsonStr = jsonStr.replace(/'([^']*)']/g, '"$1"]');  // Array last item

          // Handle Python boolean and null values
          jsonStr = jsonStr.replace(/True/g, 'true');
          jsonStr = jsonStr.replace(/False/g, 'false');
          jsonStr = jsonStr.replace(/None/g, 'null');

          return JSON.parse(jsonStr);
        } catch (e) {
          console.warn('Failed to parse Python dict:', output.substring(0, 100) + '...', 'Error:', e);
          // If all parsing fails, return the string as-is
          return output;
        }
      }
    }

    return output;
  };

  return (
    <div className="flex h-full bg-gray-50 overflow-hidden">
      {/* Left: Task List */}
      {showTaskList && (
        <div className="w-80 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
          {/* Task List Header */}
          <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
                <p className="text-xs text-gray-600 mt-1">
                  Select a task to get started
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowTaskInfoModal(true)}
                className="h-7 w-7 p-0 text-xs font-semibold text-gray-600 border-gray-300 hover:bg-gray-100"
                title="How tasks work"
              >
                ?
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tasks..."
                className="pl-9 h-8 text-sm"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex-shrink-0 px-4 py-2 border-b border-gray-200">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full h-9">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categorizedTasks).map(([categoryKey, category]) => {
                  const CategoryIcon = TASK_CATEGORY_INFO[categoryKey]?.icon;
                  return (
                    <SelectItem key={categoryKey} value={categoryKey}>
                      <div className="flex items-center gap-2">
                        {CategoryIcon && <CategoryIcon className="w-4 h-4" />}
                        <span>{category.name}</span>
                        <Badge variant="secondary" className="ml-1 h-4 text-xs px-1">
                          {category.tasks.length}
                        </Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Task List */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1 overflow-y-auto max-h-[calc(100vh-240px)]">
              {filteredTasks.map((task) => {
                const TaskIcon = task.icon;
                const isSelected = selectedTask?.id === task.id;

                return (
                  <button
                    key={task.id}
                    onClick={() => {
                      if (task.coming_soon) return;

                      // Check if task is premium-only and user is not premium
                      if (task.premium_only && accountInfo?.account_type !== 'premium') {
                        addNotification({
                          type: 'warning',
                          title: 'Premium Feature',
                          message: `${task.name} is only available for Premium subscribers. Upgrade to access this feature.`,
                          duration: 5000,
                        });
                        return;
                      }

                      setSelectedTask(task);
                      setSelectedHistoryItem(null);
                    }}
                    disabled={task.coming_soon}
                    className={`w-full text-left p-3 rounded-lg transition-all ${
                      isSelected
                        ? 'bg-blue-50 border border-blue-200'
                        : task.coming_soon
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {TaskIcon && (
                        <TaskIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                          {task.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {task.description}
                        </div>
                        {task.coming_soon && (
                          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200 mt-1 text-xs">
                            Coming Soon
                          </Badge>
                        )}
                        {task.premium_only && accountInfo?.account_type !== 'premium' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 mt-1 text-xs">
                            <Crown className="w-3 h-3 mr-1 inline" />
                            Premium
                          </Badge>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {filteredTasks.length === 0 && (
                <div className="text-center py-8 px-4">
                  <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No tasks found</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Right: Task Runner and Results */}
      <div className="flex-1 flex overflow-hidden">
        {selectedTask ? (
          <UniversalTaskPanel
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
          />
        ) : (
          <div className="flex-1 flex flex-col bg-white overflow-hidden">
            {/* All Task History Header - Hide when viewing specific history item */}
            {!selectedHistoryItem && (
              <div className="flex-shrink-0 border-b border-gray-200 px-6 py-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">All Task History</h2>
                  <Badge variant="outline" className="ml-2">
                    {allTaskHistory.length} tasks
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  View all your completed task executions
                </p>
              </div>
            )}

            {/* History Content */}
            <div className="flex-1 overflow-auto">
              <div className="p-6">
                {historyLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                    <p className="text-sm text-gray-600">Loading history...</p>
                  </div>
                ) : allTaskHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <FileText className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Task History</h3>
                    <p className="text-sm text-gray-500 max-w-sm">
                      Choose a task from the left panel to begin analyzing patents, drafting documents, or processing office actions
                    </p>
                  </div>
                ) : selectedHistoryItem ? (
                  // Display selected history item details
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h3 className="font-semibold text-lg text-gray-900">Task Result</h3>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedHistoryItem(null)}
                      >
                        Back to History
                      </Button>
                    </div>

                    {/* Task Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-white">
                            {AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || selectedHistoryItem.task_type || 'Unknown Task'}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatTimestamp(selectedHistoryItem.time_received || selectedHistoryItem.timestamp)}
                          </span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddToChat(
                            selectedHistoryItem,
                            AVAILABLE_TASKS.find(t => t.id === selectedHistoryItem.task_type)?.name || 'Task Result',
                            selectedHistoryItem.task_type,
                            selectedHistoryItem.time_received || selectedHistoryItem.timestamp
                          )}
                          className="gap-2"
                        >
                          <MessageSquarePlus className="w-4 h-4" />
                          Add to Chat
                        </Button>
                      </div>
                      {selectedHistoryItem.selected_number && (
                        <p className="text-xs text-gray-600">
                          Document: {selectedHistoryItem.selected_number}
                        </p>
                      )}
                      {selectedHistoryItem.credits_used !== undefined && (
                        <p className="text-xs text-gray-600 mt-1">
                          Credits used: {selectedHistoryItem.credits_used}
                        </p>
                      )}
                    </div>

                    {/* Result Output - Render appropriate component based on task type */}
                    {(() => {
                      const parsedData = getParsedResult(getResultData(selectedHistoryItem));
                      const commonProps = { result: parsedData, theme: 'light' as const, showHeader: true };

                      switch (selectedHistoryItem.task_type) {
                        case 'parts_list':
                          return <PartsListDisplay {...commonProps} showToggle={true} />;
                        case 'spec_map':
                          return <SpecMapDisplay {...commonProps} showToggle={true} />;
                        case 'claims_support':
                          return <ClaimsSupportDisplay {...commonProps} showToggle={true} />;
                        case 'claims_with_numerals':
                          return <ClaimsWithNumeralsDisplay {...commonProps} showToggle={true} />;
                        case 'custom_analysis':
                          return <CustomAnalysisDisplay {...commonProps} showToggle={true} />;
                        case 'ir_metadata':
                          return <IRMetadataDisplay {...commonProps} />;
                        case 'ir_innovation':
                          return <IRInnovationAnalysisDisplay {...commonProps} />;
                        case 'ir_tech_class':
                          return <IRTechClassificationDisplay {...commonProps} />;
                        case 'draft_summary':
                          return <DraftSummaryDisplay {...commonProps} />;
                        case 'draft_detailed_description':
                          return <DraftDetailedDescriptionDisplay {...commonProps} />;
                        case 'draft_background':
                          return <DraftBackgroundDisplay {...commonProps} />;
                        case 'draft_claims':
                          return <DraftClaimsDisplay {...commonProps} />;
                        case 'draft_other_embodiments':
                          return <OtherEmbodimentsDisplay {...commonProps} />;
                        case 'oa_extract':
                          return <OAExtractDisplay {...commonProps} />;
                        case 'oa_analyze':
                          return <OAAnalyzeDisplay {...commonProps} />;
                        case 'oa_summary_art':
                          return <OASummaryArtDisplay {...commonProps} />;
                        case 'oa_timeline':
                          return <OATimelineDisplay {...commonProps} />;
                        case 'translate_document':
                          return <TranslateDocumentDisplay {...commonProps} />;
                        case 'latex_to_docx':
                          return <LaTeXConvertDisplay {...commonProps} />;
                        case 'ocr_pdf':
                          return <OCRPdfDisplay {...commonProps} />;
                        case 'antecedent_basis_check':
                          return <AntecedentBasisDisplay {...commonProps} />;
                        case 'technical_outline':
                          return <TechnicalOutlineDisplay {...commonProps} />;
                        case 'parts_process_outline':
                          return <PartsProcessOutlineDisplay {...commonProps} />;
                        case 'oa_reporting_email':
                          return (
                            <div className="space-y-4">
                              {/* Email output */}
                              <div className="p-4 rounded-lg bg-white border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h3 className="font-semibold text-gray-900">Generated Email Report</h3>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                        navigator.clipboard.writeText(emailText);
                                      }}
                                      className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                      Copy to Clipboard
                                    </button>
                                    <button
                                      onClick={() => {
                                        const emailText = typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2);
                                        // Extract subject from first line if it looks like a subject line
                                        const lines = emailText.split('\n');
                                        let subject = 'Office Action Report';
                                        let body = emailText;

                                        // Check if first line is a subject
                                        const firstLine = lines[0]?.trim();
                                        if (firstLine && (firstLine.toLowerCase().startsWith('subject:') || firstLine.toLowerCase().startsWith('re:'))) {
                                          subject = firstLine.replace(/^subject:\s*/i, '').replace(/^re:\s*/i, 'Re: ');
                                          body = lines.slice(1).join('\n').trim();
                                        }

                                        // Create mailto link
                                        const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                                        window.open(mailtoUrl, '_blank');
                                      }}
                                      className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                    >
                                      Open in Email
                                    </button>
                                  </div>
                                </div>
                                <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800 leading-relaxed">
                                  {typeof parsedData === 'string' ? parsedData : JSON.stringify(parsedData, null, 2)}
                                </pre>
                              </div>
                            </div>
                          );
                        default:
                          return (
                            <div className="p-4 rounded-lg bg-gray-50">
                              <pre className="text-xs overflow-auto whitespace-pre-wrap">
                                {JSON.stringify(parsedData, null, 2)}
                              </pre>
                            </div>
                          );
                      }
                    })()}
                  </div>
                ) : (
                  // List of all history items
                  <div className="space-y-2">
                    {allTaskHistory.map((item, index) => {
                      // Try to find task by task_type first, then by id (for legacy tasks)
                      const taskInfo = AVAILABLE_TASKS.find(t => t.task_type === item.task_type || t.id === item.task_type);
                      const TaskIcon = taskInfo?.icon;

                      return (
                        <button
                          key={item.analysis_id || index}
                          onClick={() => setSelectedHistoryItem(item)}
                          className="w-full text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm transition-all"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              {TaskIcon && (
                                <div className="flex-shrink-0 mt-1">
                                  <TaskIcon className="w-4 h-4 text-gray-400" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {taskInfo?.name || item.task_type || 'Unknown Task'}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {formatTimestamp(item.time_received || item.timestamp)}
                                </p>
                                {item.selected_number && (
                                  <p className="text-xs text-gray-400 mt-1 truncate">
                                    Document: {item.selected_number}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex-shrink-0">
                              <Badge variant="outline" className="text-xs">
                                {item.credits_used || 0} credits
                              </Badge>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showTaskInfoModal} onOpenChange={setShowTaskInfoModal}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-gray-900">How Tasks Work</DialogTitle>
            <DialogDescription className="text-gray-600">
              Tasks are guided workflows that package proven prompts and output layouts so you can generate consistent patent
              deliverables with a few clicks.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-600">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">What happens when you run a task</h4>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Pick a task and fill in any requested inputs (claims, doc selections, focus instructions).</li>
                <li>After submission, a task result is generated that you can view or insert into chat for further analysis.</li>
              </ol>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Tips</h4>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Use the search and category filter to quickly find the workflow you need.</li>
                <li>Task history keeps every run—open a result to review details or push it back into the chat.</li>
                <li>Select the documents/resources or insert text into the task input and then select submit to run the task.</li>
                <li>Tasks incur credits based on the size and complexity of the task.</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
